import React, { useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------- #
// RegisterGrid
// ---------------------------------------------------------------- #
//
// The Integrations Register grid: one row per park, one column per product
// (PMS, Bookkeeping, Analytics, Business, Facebook, Instagram), a park
// search box, and a legend. Every state has a colour, a symbol and a text
// label in the tooltip, so colour is never the only signal. The tooltip is a
// small dark box with a caret pointing down at the cell, drawn at a fixed
// position so the grid's scroll box cannot clip it.
//
// Props:
//   register     The `data` of GET register
//   onCellClick  Called with (park, productKey, cell) when an editable cell is clicked

// ---------------------------------------------------------------- #
// States and constants
// ---------------------------------------------------------------- #

/** How each cell state is drawn: symbol, text label and colour class. */
const CELL_STATES = {
  'complete': { symbol: '●', label: 'Complete', colourClass: 'is-complete' },
  'in-review': { symbol: '◐', label: 'In review', colourClass: 'is-in-review' },
  'needs-action': { symbol: '▲', label: 'Needs action', colourClass: 'is-needs-action' },
  'gap': { symbol: '○', label: 'Not set up', colourClass: 'is-gap' },
  'not-needed': { symbol: '-', label: 'Not needed', colourClass: 'is-not-needed' },
  'not-in-plan': { symbol: '', label: '', colourClass: 'is-not-in-plan' },  // we don't need a label for not in plan
  'contact-support': { symbol: '', label: 'Contact support', colourClass: 'is-contact-support' }
};

/** Order the legend lists the states in. */
const LEGEND_ORDER = ['complete', 'in-review', 'needs-action', 'gap', 'not-needed', 'not-in-plan'];

/** With more parks than this, the search box is focused when the grid loads. */
const FOCUS_SEARCH_PARK_COUNT = 25;

/** Fallback drawing for a state the API sends that this file does not know. */
const UNKNOWN_STATE = { symbol: '?', label: 'Unknown', colourClass: 'is-not-in-plan' };

/** States whose tooltip adds the reason under the label; the others need only the label. */
const STATES_WITH_REASON = ['needs-action', 'in-review', 'contact-support'];

/** Gap in pixels between the tooltip's caret and the cell it points at. */
const TOOLTIP_GAP = 8;

// ---------------------------------------------------------------- #
// Cell helpers
// ---------------------------------------------------------------- #

/**
 * Look up how to draw a cell state.
 */
const getStateStyle = (state) => {
  const style = CELL_STATES[state];
  if (style) {
    return style;
  }
  return UNKNOWN_STATE;
};

/**
 * Build a cell's tooltip: the state label, plus the reason only for states that need explaining.
 * Returns { title, detail }, detail being '' when there is nothing to add.
 */
const buildCellTip = (cell) => {
  // The label alone says what the user needs to know for most states
  const stateStyle = getStateStyle(cell.state);
  const title = stateStyle.label;

  // Needs action, in review and contact support also say why
  let detail = '';
  const wantsReason = STATES_WITH_REASON.includes(cell.state);
  if (wantsReason && cell.reason) {
    detail = cell.reason;
  }

  return { title: title, detail: detail };
};

/**
 * Pick the short provider name shown inside PMS and Bookkeeping cells.
 * A park that does not need the integration shows no provider, only the "not needed" dash.
 */
const getCellProviderName = (productKey, cell) => {
  // Nothing is shown when the integration is not needed
  if (cell.state === 'not-needed') {
    return '';
  }

  // Nothing is shown for cells without a mapping
  if (!cell.mapping) {
    return '';
  }

  // PMS shows RMS or Newbook
  if (productKey === 'pms') {
    return cell.mapping.application || '';
  }

  // Bookkeeping shows Xero or MYOB
  if (productKey === 'bookkeeping') {
    return cell.mapping.provider || '';
  }

  return '';
};

// ---------------------------------------------------------------- #
// The grid
// ---------------------------------------------------------------- #

/**
 * The parks by products grid with search and legend.
 */
const RegisterGrid = ({ register, onCellClick }) => {
  const [search, setSearch] = useState('');
  const [tip, setTip] = useState(null);
  const searchInputRef = useRef(null);

  // Read the products and parks, defaulting to empty lists
  const products = register?.products || [];
  const parks = register?.parks || [];

  // Focus the search box on load when there are many parks
  const hasManyParks = parks.length > FOCUS_SEARCH_PARK_COUNT;
  useEffect(() => {
    if (hasManyParks && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [hasManyParks]);

  // Hide the tooltip when the page scrolls, because it is placed against the cell's position on screen
  const isTipShown = tip !== null;
  useEffect(() => {
    if (!isTipShown) {
      return undefined;
    }
    const hideOnScroll = () => setTip(null);
    window.addEventListener('scroll', hideOnScroll, true);
    return () => window.removeEventListener('scroll', hideOnScroll, true);
  }, [isTipShown]);

  /**
   * Show a cell's tooltip centred above the cell that was hovered or focused.
   */
  const showTip = (event, cellTip) => {
    const cellBox = event.currentTarget.getBoundingClientRect();
    const left = cellBox.left + cellBox.width / 2;
    const top = cellBox.top - TOOLTIP_GAP;
    setTip({ title: cellTip.title, detail: cellTip.detail, left: left, top: top });
  };

  /**
   * Hide the tooltip.
   */
  const hideTip = () => {
    setTip(null);
  };

  // Keep only the parks whose name matches the search
  const searchText = search.trim().toLowerCase();
  const visibleParks = [];
  for (const park of parks) {
    const parkName = (park.parkName || '').toLowerCase();
    const matchesSearch = searchText === '' || parkName.includes(searchText);
    if (!matchesSearch) {
      continue;
    }
    visibleParks.push(park);
  }

  /**
   * Draw one cell of the grid.
   */
  const renderCell = (park, product) => {
    // A park with no cell for this product is drawn as not in plan
    let cell = park.cells?.[product.key];
    if (!cell) {
      cell = { state: 'not-in-plan', editable: false, summary: '', reason: null };
    }

    // Work out what the cell shows
    const stateStyle = getStateStyle(cell.state);
    const cellTip = buildCellTip(cell);
    const providerName = getCellProviderName(product.key, cell);
    const isTextState = cell.state === 'not-in-plan' || cell.state === 'contact-support';

    // The words a screen reader hears for the cell
    let spokenText = `${park.parkName}, ${product.label}: ${cellTip.title}`;
    if (cellTip.detail) {
      spokenText += `. ${cellTip.detail}`;
    }

    // Cells that cannot be changed are plain text with the reason on hover
    if (!cell.editable) {
      return (
        <td key={product.key} className="integrations-grid-cell">
          <span
            className={`integrations-cell-state ${stateStyle.colourClass}`}
            onMouseEnter={(event) => showTip(event, cellTip)}
            onMouseLeave={hideTip}
          >
            {stateStyle.label}
            {cellTip.detail && <span className="visually-hidden">. {cellTip.detail}</span>}
          </span>
        </td>
      );
    }

    // Editable cells are buttons that open the wizard
    return (
      <td key={product.key} className="integrations-grid-cell">
        <button
          type="button"
          className={`integrations-cell-button ${stateStyle.colourClass}`}
          aria-label={spokenText}
          onMouseEnter={(event) => showTip(event, cellTip)}
          onMouseLeave={hideTip}
          onFocus={(event) => showTip(event, cellTip)}
          onBlur={hideTip}
          onClick={() => {
            hideTip();
            onCellClick(park, product.key, cell);
          }}
        >
          {providerName && (
            <span className="integrations-cell-provider">{providerName}</span>
          )}
          {!isTextState && (
            <span className="integrations-cell-symbol" aria-hidden="true">{stateStyle.symbol}</span>
          )}
        </button>
      </td>
    );
  };

  // Build the legend items in their fixed order
  const legendItems = [];
  for (const state of LEGEND_ORDER) {
    const stateStyle = CELL_STATES[state];
    legendItems.push(
      <span key={state} className={`integrations-legend-item ${stateStyle.colourClass}`}>
        {stateStyle.symbol && (
          <span className="integrations-cell-symbol me-1" aria-hidden="true">{stateStyle.symbol}</span>
        )}
        {stateStyle.label}
      </span>
    );
  }

  return (
    <div className="card integrations-grid-card">
      {/* Title and search */}
      <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
        <h5 className="mb-0">Parks</h5>
        <div className="integrations-grid-search">
          <input
            ref={searchInputRef}
            type="search"
            className="form-control"
            placeholder="Search parks"
            aria-label="Search parks"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>

      {/* The grid itself; it scrolls inside its own box so the header row can stay pinned at the top */}
      <div className="card-body integrations-grid-body">
        <div className="table-responsive integrations-grid-scroll">
          <table className="table integrations-grid mb-0">
            <thead>
              <tr>
                <th scope="col">Parks</th>
                {products.map(product => (
                  <th key={product.key} scope="col" className="text-center">{product.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleParks.map(park => (
                <tr key={park.parkiId}>
                  <th scope="row" className="integrations-grid-park">{park.parkName}</th>
                  {products.map(product => renderCell(park, product))}
                </tr>
              ))}
              {visibleParks.length === 0 && (
                <tr>
                  <td colSpan={products.length + 1} className="text-center py-4">
                    {parks.length === 0 ? 'No parks with an active subscription.' : 'No parks match your search.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="integrations-legend mt-3">
          {legendItems}
        </div>
      </div>

      {/* The tooltip for the hovered or focused cell, with its caret pointing down at the cell */}
      {tip && (
        <div className="integrations-tooltip" role="tooltip" style={{ left: tip.left, top: tip.top }}>
          <div className="integrations-tooltip-title">{tip.title}</div>
          {tip.detail && <div className="integrations-tooltip-detail">{tip.detail}</div>}
        </div>
      )}
    </div>
  );
};

export default RegisterGrid;
